import { FC, RefObject, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, TypographyProps } from '@kizen/kds/Typography';
import { Button } from '@kizen/kds/Button';
import { ValidIcons } from '@kizen/kds/Icon';
import { deferExecution } from 'utility/defer';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import Loader from '__components/Kizen/Loader';
import { tableHover } from 'app/colors';
import { MessageType } from './index';

import {
  MessageHistoryWrapper,
  GradientTopWrapper,
  GradientBottomWrapper,
  MessageWrapper,
  Message,
  ResponseLoadingWrapper,
  LoadingDotsWrapper,
  Dot,
  MessageTop,
  MessageBottom,
  MesssageHeader,
} from './styles';

interface MessageHistoryProps {
  isMobile: boolean;
  fullScreen: boolean;
  sidePadding: string;
  topPadding: string;
  messageHistoryWrapperRef: RefObject<HTMLDivElement>;
  isLoadingNewSession: boolean;
  isMessageResponseLoading: boolean;
  messages: MessageType[];
  floatingResponseOptions: string[];
  inModal?: boolean;
  shrinkHeightAmount?: number;
}

export const TypingIndicator = ({
  label,
  size = 'md',
  color = 'gray-700',
  weight = 'regular',
  inline = false,
}: {
  label: string | null;
  size?: TypographyProps['size'];
  color?: TypographyProps['color'];
  weight?: TypographyProps['weight'];
  inline?: boolean;
}) => (
  <ResponseLoadingWrapper inline={inline}>
    <Typography color={color} size={size} weight={weight}>
      {label}
    </Typography>
    <LoadingDotsWrapper>
      <Dot style={{ animationDelay: '0s' }} color={color} />
      <Dot style={{ animationDelay: '0.1s' }} color={color} />
      <Dot style={{ animationDelay: '0.2s' }} color={color} />
    </LoadingDotsWrapper>
  </ResponseLoadingWrapper>
);

const ExpanableMessage = ({
  alignment,
  header,
  messageContent,
}: {
  alignment: 'left' | 'right';
  header: string;
  messageContent: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <MessageWrapper alignment={alignment}>
      <Message
        color="rgba(var(--color-teal-300-rgb-partial), 0.1)"
        isSender={false}
      >
        <MessageTop>
          <MesssageHeader>
            <Typography
              enableTooltip={false}
              weight="semibold"
              color="font-primary"
            >
              {header}
            </Typography>
            <Button
              rightIcon={
                (isExpanded
                  ? 'form-input-dropdown-arrow-up'
                  : 'form-input-dropdown-arrow') as ValidIcons
              }
              rightIconSettings={{
                tooltipTitleOverride: isExpanded ? 'Collapse' : 'Expand',
                color: 'font-primary',
              }}
              hoverColor="rgba(var(--color-gray-800)"
              variant="text"
              color="inherit"
              onClick={() => setIsExpanded(!isExpanded)}
            />
          </MesssageHeader>
        </MessageTop>
        {isExpanded ? (
          <MessageBottom>
            <Typography enableTooltip={false} color="font-primary">
              {messageContent}
            </Typography>
          </MessageBottom>
        ) : null}
      </Message>
    </MessageWrapper>
  );
};

export const MessageHistory: FC<MessageHistoryProps> = ({
  isMobile,
  fullScreen,
  inModal = false,
  sidePadding,
  topPadding,
  messageHistoryWrapperRef,
  isLoadingNewSession,
  isMessageResponseLoading,
  floatingResponseOptions,
  messages,
  shrinkHeightAmount,
}) => {
  const [isScrolledTop, setIsScrolledTop] = useState(true);
  const [isScrolledBottom, setIsScrolledBottom] = useState(true);
  const [bottomGradientPosition, setBottomGradientPosition] = useState('135px');

  const messageRef = useRef<MessageType[]>(messages);
  const floatingResponseRef = useRef<string[]>(floatingResponseOptions);
  const intialScrollToBottomRef = useRef<boolean>(true);
  const { t } = useTranslation();

  // set inital scroll position to last message
  if (intialScrollToBottomRef.current && messageHistoryWrapperRef.current) {
    messageHistoryWrapperRef.current.scrollTop =
      messageHistoryWrapperRef.current.scrollHeight;
    intialScrollToBottomRef.current = false;
  }

  // scroll to last message if we are scrolled to bottom
  useEffect(() => {
    const priorLastMessage = messageRef.current[messageRef.current.length - 1];
    const newLastMessage = messages[messages.length - 1];
    if (
      messageRef.current.length &&
      messageRef.current.length != messages.length &&
      priorLastMessage?.id !== newLastMessage?.id &&
      priorLastMessage?.messageContent.length !==
        newLastMessage?.messageContent.length
    ) {
      if (isScrolledBottom) {
        deferExecution(() =>
          messageHistoryWrapperRef.current?.scrollTo({
            top: messageHistoryWrapperRef.current.scrollHeight,
            behavior: 'smooth',
          })
        );
      }
      floatingResponseRef.current = floatingResponseOptions;
      messageRef.current = messages;
    }
  }, [
    floatingResponseOptions,
    messages,
    isMessageResponseLoading,
    messageHistoryWrapperRef,
    isScrolledBottom,
  ]);

  // scroll to bottom when response is loading
  useEffect(() => {
    const priorLastMessage = messageRef.current[messageRef.current.length - 1];
    const newLastMessage = messages[messages.length - 1];
    if (
      priorLastMessage?.isResponseLoading !=
        newLastMessage?.isResponseLoading ||
      newLastMessage?.id === 'refining' ||
      newLastMessage?.id === 'loading' ||
      isMessageResponseLoading
    ) {
      if (isScrolledBottom) {
        deferExecution(() =>
          messageHistoryWrapperRef.current?.scrollTo({
            top: messageHistoryWrapperRef.current.scrollHeight,
            behavior: 'smooth',
          })
        );
      }
    }
  }, [
    isMessageResponseLoading,
    messages,
    messageHistoryWrapperRef,
    isScrolledBottom,
  ]);

  // calculate if scrolled to top
  useEffect(() => {
    const currentRef = messageHistoryWrapperRef.current;
    const handleScroll = () => {
      setIsScrolledTop(currentRef?.scrollTop === 0);
    };

    currentRef?.addEventListener('scroll', handleScroll);

    return () => {
      currentRef?.removeEventListener('scroll', handleScroll);
    };
  }, [messageHistoryWrapperRef]);

  // calculate if scrolled to bottom
  useEffect(() => {
    const currentRef = messageHistoryWrapperRef.current;
    const handleScroll = () => {
      if (currentRef) {
        const isScrolledBottom =
          currentRef?.scrollHeight - currentRef?.scrollTop <=
          currentRef?.clientHeight + 30; // Add a small threshold
        setIsScrolledBottom(isScrolledBottom);
      }
    };

    currentRef?.addEventListener('scroll', handleScroll);

    return () => {
      currentRef?.removeEventListener('scroll', handleScroll);
    };
  }, [messageHistoryWrapperRef]);

  // ensure bottom graident is always at the bottom of message history container
  useEffect(() => {
    const currentRef = messageHistoryWrapperRef.current;
    if (!currentRef) return;

    const resizeObserver = new ResizeObserver(() => {
      const height = currentRef.clientHeight;
      setBottomGradientPosition(`${height + 10}px`);
    });

    resizeObserver.observe(currentRef);

    return () => {
      resizeObserver.disconnect();
    };
  }, [messageHistoryWrapperRef]);

  return (
    <>
      <MessageHistoryWrapper
        className="kds-expanding-scrollbar"
        ref={messageHistoryWrapperRef}
        loading={isLoadingNewSession}
        isAutoResponsesDisplayed={
          !isMessageResponseLoading && floatingResponseOptions?.length > 0
        }
        fullHeight={fullScreen}
        isMobile={isMobile}
        sidePadding={sidePadding}
        topPadding={topPadding}
        shrinkHeightAmount={shrinkHeightAmount}
      >
        <GradientTopWrapper borderRadius={fullScreen || inModal ? '0' : '10px'}>
          <Gradient
            fadeStart={0}
            fadeLength={70}
            color={topPadding ? '#FFFFFF' : tableHover}
            position="top"
            fadeIn={!isScrolledTop}
            zIndex={2}
            borderRadius={fullScreen || inModal ? '0' : '10px 10px 0 0'}
          />
        </GradientTopWrapper>
        {isLoadingNewSession && !isMessageResponseLoading ? (
          <Loader loading={true} className="new-chat-loader" />
        ) : null}
        {!isLoadingNewSession &&
          messages?.map(
            ({
              id,
              messageContent,
              isUserMessage,
              isResponseLoading,
              isResponseRefining,
              isCollapsable,
              header = '',
            }) => {
              if (isResponseLoading || isResponseRefining) {
                let label = t('Thinking');
                if (isResponseRefining) {
                  label = t('Refining');
                }
                return <TypingIndicator key="loader" label={label} />;
              }
              if (isCollapsable) {
                return (
                  <ExpanableMessage
                    key={id}
                    alignment={isUserMessage ? 'right' : 'left'}
                    header={header}
                    messageContent={messageContent}
                  />
                );
              }
              return (
                <MessageWrapper
                  key={id}
                  alignment={isUserMessage ? 'right' : 'left'}
                  className="message"
                >
                  <Message
                    color={
                      isUserMessage
                        ? 'rgba(var(--color-blue-300-rgb-partial), 0.1)'
                        : 'rgba(var(--color-teal-300-rgb-partial), 0.1)'
                    }
                    isSender={isUserMessage}
                  >
                    <Typography enableTooltip={false} color="gray-800">
                      {messageContent}
                    </Typography>
                  </Message>
                </MessageWrapper>
              );
            }
          )}
        <GradientBottomWrapper topPosition={bottomGradientPosition}>
          <Gradient
            fadeStart={0}
            fadeLength={70}
            color="#FFFFFF"
            position="bottom"
            fadeIn={!isScrolledBottom}
          />
        </GradientBottomWrapper>
      </MessageHistoryWrapper>
    </>
  );
};
